import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Button, Pill } from "../../components/common/Primitives";
import { Download, ChevronLeft, Loader2, FileText, Lock } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { ReportView } from "../admin/DDReports";
import DeepDDRequestCard from "../../components/investor/DeepDDRequestCard";

const LABEL_TONE = {
  Excellent: "success", Strong: "success", Emerging: "gold", Adequate: "gold",
  Weak: "warn", Concerning: "danger", "Insufficient Data": "muted",
};
const VERDICT_TONE = {
  PROCEED: "success", "PROCEED WITH DILIGENCE": "gold", REVIEW: "warn", HOLD: "danger",
};
const REPORT_LABEL = { health_check: "Basic Health Check", detailed_dd: "Detailed Due Diligence" };


export function InvestorDDReportsList() {
  const { startupId } = useParams();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/investor/dd-reports/${startupId}`)
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .catch((e) => {
        const detail = e?.response?.data?.detail || formatApiError(e);
        setError(detail);
        setItems([]);
      });
    return () => { cancelled = true; };
  }, [startupId]);

  return (
    <DashboardShell
      title="Diligence Reports"
      subtitle={`STARTUP · ${startupId}`}
      actions={
        <Link to="/investor/marketplace" className="text-sm text-[#8A6600] hover:underline" data-testid="investor-dd-back">
          <ChevronLeft size={12} className="inline" /> Back to marketplace
        </Link>
      }
    >
      <Toaster richColors position="top-right" />

      <div className="mb-4">
        <DeepDDRequestCard startupId={startupId} startupName={`Startup ${startupId?.slice(-6) || ""}`} />
      </div>

      {items === null && (
        <div className="text-ink-muted"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
      )}

      {error && (
        <Card testId="investor-dd-error-card">
          <div className="flex items-center gap-2 text-rose-700">
            <Lock size={16} /> <span className="text-sm">{error}</span>
          </div>
          <div className="text-xs text-ink-muted mt-2">
            Express interest in this startup (or wait until you share an active deal room) to unlock its diligence reports.
          </div>
        </Card>
      )}

      {items && !error && items.length === 0 && (
        <Card testId="investor-dd-empty-card">
          <div className="text-sm text-ink-muted">
            No diligence reports have been shared yet by VenturaZ for this startup. Check back later.
          </div>
        </Card>
      )}

      {items && items.length > 0 && (
        <div className="grid gap-3">
          {items.map((r) => (
            <Card key={r.report_id} testId={`investor-dd-card-${r.report_id}`} className="p-0 overflow-hidden">
              <div className="p-4 flex items-center justify-between gap-3 border-b border-navy/[0.06]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-navy/5 flex items-center justify-center text-navy"><FileText size={16} /></div>
                  <div>
                    <div className="font-heading text-ink">{REPORT_LABEL[r.report_type]}</div>
                    <div className="text-[11px] font-mono text-ink-muted">{r.report_id} · shared {r.generated_at?.slice(0, 10)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Pill tone={LABEL_TONE[r.label] || "muted"}>{r.label || "—"}</Pill>
                  <Pill tone={VERDICT_TONE[r.verdict] || "muted"}>{r.verdict || "—"}</Pill>
                  <Link
                    to={`/investor/dd-reports/${startupId}/${r.report_id}`}
                    className="text-sm text-[#8A6600] font-heading hover:underline"
                    data-testid={`investor-dd-view-${r.report_id}`}
                  >View →</Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}


export function InvestorDDReportDetail() {
  const { startupId, reportId } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/investor/dd-reports/${startupId}/${reportId}`)
      .then(({ data }) => { if (!cancelled) setReport(data); })
      .catch((e) => setError(e?.response?.data?.detail || formatApiError(e)));
    return () => { cancelled = true; };
  }, [startupId, reportId]);

  const download = async () => {
    try {
      const res = await api.get(`/investor/dd-reports/${startupId}/${reportId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `venturaz-report-${reportId.slice(-8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell
      title={report ? `${REPORT_LABEL[report.report_type]} · ${report.startup_name || startupId}` : "Diligence Report"}
      subtitle={reportId}
      actions={
        <div className="flex gap-2">
          {report && <Button variant="primary" onClick={download} data-testid="investor-dd-download-pdf"><Download size={12} className="inline mr-1" /> PDF</Button>}
          <Link to={`/investor/dd-reports/${startupId}`} className="text-sm text-[#8A6600] hover:underline self-center" data-testid="investor-dd-back-list">← All reports</Link>
        </div>
      }
    >
      <Toaster richColors position="top-right" />
      {error && (
        <Card testId="investor-dd-detail-error"><span className="text-sm text-rose-700">{error}</span></Card>
      )}
      {!report && !error && <div className="text-ink-muted"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>}
      {report && <ReportView report={report.data} />}
    </DashboardShell>
  );
}
